/**
 * MCP bridge client — the in-app half of the relay protocol.
 *
 * Lifecycle: while the bridge is enabled the client registers with the
 * local Node MCP relay (/bridge/hello), then long-runs a poll loop
 * (/bridge/poll). Every queued agent request executes through the local
 * tool router and returns via /bridge/result. Connection state and the
 * optional per-request approval gate are exposed through the zustand
 * stores so the Settings pane and StatusBar can render them.
 */

import { create } from 'zustand';
import {
    bridgeBaseUrl,
    type BridgeHelloPayload,
    type BridgePollResponse,
    type BridgeRequest,
} from './mcpBridgeProtocol';
import { MCP_TOOL_CATALOG } from './mcpToolCatalog';
import { executeMcpTool } from './mcpToolRouter';
import { useMcpBridgeStore } from './useMcpBridgeStore';
import { useAppStore } from '../../state/store';

const POLL_INTERVAL_MS = 500;
const RECONNECT_BASE_MS = 1500;
const RECONNECT_MAX_MS = 10000;
/** Requests older than this are dropped: the agent already moved on. */
const STALE_REQUEST_MS = 120000;

/** Tools that change the document or viewport — subject to the approval gate. */
const MUTATING_TOOLS = new Set([
    'yap_execute_command',
    'yap_run_script',
    'yap_create_element',
    'yap_update_element',
    'yap_delete_elements',
    'yap_select_elements',
    'yap_control_view',
    'yap_manage_layers',
    'yap_document_action',
    'yap_set_mode',
    'yap_set_tool_option',
    // Family surface: import registers families and placement inserts elements
    // — both mutate the project, so they must pass the gate like create/update.
    'yap_import_ifc',
    'yap_place_family',
]);

export function isMutatingTool(tool: string): boolean {
    return MUTATING_TOOLS.has(tool);
}

// ---------------------------------------------------------------------------
// approval gate — pending requests shown by McpBridgeHost UI
// ---------------------------------------------------------------------------

interface ApprovalItem {
    id: string;
    tool: string;
    summary: string;
}

interface McpApprovalState {
    pending: ApprovalItem[];
    resolve: (id: string, approved: boolean) => void;
}

const approvalWaiters = new Map<string, (approved: boolean) => void>();

export const useMcpApprovalStore = create<McpApprovalState>((set, get) => ({
    pending: [],
    resolve: (id, approved) => {
        const waiter = approvalWaiters.get(id);
        approvalWaiters.delete(id);
        set({ pending: get().pending.filter((p) => p.id !== id) });
        waiter?.(approved);
    },
}));

function requestApproval(item: ApprovalItem): Promise<boolean> {
    return new Promise((resolve) => {
        approvalWaiters.set(item.id, resolve);
        useMcpApprovalStore.setState((s) => ({ pending: [...s.pending, item] }));
    });
}

// ---------------------------------------------------------------------------
// loopback gate
// ---------------------------------------------------------------------------

/** The chrome-served probes (`suite-mcp.js` on the hub origin, suite-mcp-contract §5). */
interface SuiteMcpGlobal {
    isLoopbackHost(hostname: string): boolean;
}

function sharedMcp(): SuiteMcpGlobal | null {
    if (typeof window === 'undefined') return null;
    return (window as unknown as { SuiteMcp?: SuiteMcpGlobal }).SuiteMcp ?? null;
}

/**
 * True for hostnames the browser treats as this machine's own loopback.
 * The chrome-shared probe decides; the inline fallback answers identically
 * when the document's chrome assets failed to load — a gating probe must
 * not become safe-by-accident on a network failure.
 */
function isLoopbackHost(hostname: string): boolean {
    return (
        sharedMcp()?.isLoopbackHost(hostname) ??
        /^(localhost|127\.0\.0\.1|\[::1\]|::1)$/i.test(hostname)
    );
}

// ---------------------------------------------------------------------------
// transport helpers
// ---------------------------------------------------------------------------

async function postJson(url: string, body: unknown): Promise<Response> {
    return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });
}

function summarizeArgs(request: BridgeRequest): string {
    const keys = Object.keys(request.args ?? {});
    if (keys.length === 0) return 'no arguments';
    return keys
        .slice(0, 4)
        .map((k) => {
            const v = request.args[k];
            const s = typeof v === 'string' ? v : JSON.stringify(v);
            return `${k}=${s && s.length > 60 ? `${s.slice(0, 60)}…` : s}`;
        })
        .join(', ');
}

/**
 * A fetch failure against the loopback relay means "no relay process there
 * yet" — the normal state until `npm run mcp` starts one or the Ortam hub
 * spawns it for an agent chat. Say that in actionable terms instead of
 * surfacing a raw browser fetch error as the bridge's lastError.
 */
function describeBridgeError(error: unknown, url: string): string {
    const raw = error instanceof Error ? error.message : String(error);
    if (/failed to fetch|networkerror|load failed|econnrefused/i.test(raw)) {
        return `No MCP relay is listening on ${url} — start one with "npm run mcp" in the yap folder, or open an Ortam agent chat (the hub spawns the relay on demand).`;
    }
    return raw;
}

// ---------------------------------------------------------------------------
// client loop
// ---------------------------------------------------------------------------

let running = false;
let stopToken = 0;
let clientSeq = 0;

/** Fresh tab-scoped id; the relay keys its queue on it. */
function makeClientId(): string {
    clientSeq += 1;
    return `yap-app-${Date.now().toString(36)}-${clientSeq}`;
}

async function runClientLoop(token: number): Promise<void> {
    const store = () => useMcpBridgeStore.getState();
    const baseUrl = () => bridgeBaseUrl(store().port);
    const clientId = makeClientId();
    let backoff = RECONNECT_BASE_MS;

    // The bridge is loopback-only; do not attempt connections from public origins.
    if (typeof window !== 'undefined' && !isLoopbackHost(window.location.hostname)) {
        useMcpBridgeStore.getState().setConnection('disabled', 'MCP bridge is loopback-only - not available from public origin');
        return;
    }

    while (running && token === stopToken) {
        const { requireApproval } = store();

        // --- handshake ---
        const hello: BridgeHelloPayload = {
            clientId,
            appName: 'Yap',
            appVersion: '1.0.0',
            mode: useAppStore.getState().mode,
            tools: MCP_TOOL_CATALOG,
        };
        try {
            const res = await postJson(`${baseUrl()}/bridge/hello`, hello);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            backoff = RECONNECT_BASE_MS;
            if (store().connection !== 'connected') {
                store().setConnection('connected');
            }
        } catch (error) {
            store().setConnection('offline', describeBridgeError(error, `${baseUrl()}/bridge/hello`));
            await sleep(backoff, token);
            backoff = Math.min(backoff * 2, RECONNECT_MAX_MS);
            continue;
        }

        // --- poll loop (until the connection breaks) ---
        while (running && token === stopToken) {
            try {
                const res = await postJson(`${baseUrl()}/bridge/poll`, { clientId });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const payload = (await res.json()) as BridgePollResponse;
                if (payload.idle) {
                    await sleep(POLL_INTERVAL_MS, token);
                    continue;
                }
                await handleRequest(payload, requireApproval, baseUrl(), clientId);
            } catch (error) {
                if (!running || token !== stopToken) break;
                store().setConnection('offline', describeBridgeError(error, `${baseUrl()}/bridge/poll`));
                break;
            }
        }
    }
}

async function handleRequest(
    request: BridgeRequest,
    requireApproval: boolean,
    baseUrl: string,
    clientId: string,
): Promise<void> {
    if (Date.now() - request.queuedAt > STALE_REQUEST_MS) {
        await postJson(`${baseUrl}/bridge/result`, {
            id: request.id,
            clientId,
            result: { ok: false, error: 'Request expired in the relay queue before the app could serve it.' },
        }).catch(() => undefined);
        return;
    }

    if (requireApproval && isMutatingTool(request.tool)) {
        const approved = await requestApproval({
            id: request.id,
            tool: request.tool,
            summary: summarizeArgs(request),
        });
        if (!approved) {
            await postJson(`${baseUrl}/bridge/result`, {
                id: request.id,
                clientId,
                result: { ok: false, error: 'The user rejected this MCP request in the approval dialog.' },
            }).catch(() => undefined);
            return;
        }
    }

    const result = await executeMcpTool(request.tool, request.args ?? {});
    useMcpBridgeStore.getState().recordServed();
    await postJson(`${baseUrl}/bridge/result`, {
        id: request.id,
        clientId,
        result,
    });
}

function sleep(ms: number, token: number): Promise<void> {
    return new Promise((resolve) => {
        const t = window.setTimeout(resolve, ms);
        // If the loop is stopped mid-backoff, wake up promptly.
        const check = window.setInterval(() => {
            if (!running || token !== stopToken) {
                window.clearTimeout(t);
                window.clearInterval(check);
                resolve();
            }
        }, 150);
        window.setTimeout(() => window.clearInterval(check), ms + 200);
    });
}

let unsubscribe: (() => void) | null = null;

/** Start (or restart) the bridge client. Safe to call repeatedly. */
export function startMcpBridge(): void {
    stopMcpBridge();
    // React to settings changes: enable/disable and port switches restart.
    unsubscribe = useMcpBridgeStore.subscribe((state, prev) => {
        if (state.enabled !== prev.enabled || state.port !== prev.port) {
            restartLoops();
        }
    });
    restartLoops();
}

function restartLoops(): void {
    const { enabled } = useMcpBridgeStore.getState();
    running = false;
    stopToken += 1;
    if (!enabled) {
        useMcpBridgeStore.getState().setConnection('disabled');
        return;
    }
    running = true;
    useMcpBridgeStore.getState().setConnection('connecting');
    void runClientLoop(stopToken);
}

/** Stop the client and detach the settings subscription. */
export function stopMcpBridge(): void {
    running = false;
    stopToken += 1;
    unsubscribe?.();
    unsubscribe = null;
}
